// 새 버전 배포 감지 — 열어 둔 탭이 옛 JS 로 계속 도는 것을 화면 이동 때 전체 새로고침으로 바꾼다 (2026-10-23)
//
// 빌드 버전(VUE_APP_BUILD_VERSION, vue.config.js 에서 주입)과 같은 값을 빌드 산출물 /version.json 으로 내보낸다.
// 화면을 옮길 때 서버의 version.json 을 읽어 다르면 이동하려던 경로로 문서를 새로 받는다.
// 실패(네트워크·404·JSON 오류·시간 초과)면 아무것도 하지 않는다 — 셀러 화면을 막지 않는다.

export const VERSION_CHECK_INTERVAL_MS = 60 * 1000
export const VERSION_FETCH_TIMEOUT_MS = 3000
// 새로고침 URL 의 캐시 깨기 쿼리 — 새 문서가 뜨면 주소창에서 지운다
export const RELOAD_QUERY_KEY = '_v'
// 이 탭에서 이미 새로고침한 서버 버전 — 새로고침 뒤에도 옛 코드면 같은 버전으로 다시 새로고침하지 않는다(무한 루프 방지)
export const RELOADED_VERSION_STORAGE_KEY = 'appVersionReloadedTo'

let lastCheckedAt = 0

export function currentAppVersion(): string {
  return process.env.VUE_APP_BUILD_VERSION || ''
}

async function fetchServerVersion(): Promise<string | null> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), VERSION_FETCH_TIMEOUT_MS) : null
  try {
    const res = await fetch(`${process.env.BASE_URL || '/'}version.json?t=${Date.now()}`, {
      cache: 'no-store',
      signal: controller?.signal,
    })
    if (!res.ok) return null
    const body = await res.json()
    return typeof body?.version === 'string' && body.version ? body.version : null
  } catch {
    return null
  } finally {
    if (timer) clearTimeout(timer)
  }
}

function readReloadedVersion(): string | null {
  try {
    return sessionStorage.getItem(RELOADED_VERSION_STORAGE_KEY)
  } catch {
    return null
  }
}

function markReloaded(version: string) {
  try {
    sessionStorage.setItem(RELOADED_VERSION_STORAGE_KEY, version)
  } catch {
    // sessionStorage 를 못 쓰면 표시 없이 진행 — 아래에서 표시를 못 남기면 새로고침하지 않는다
  }
}

export function reloadUrl(fullPath: string, serverVersion: string): string {
  const url = new URL(fullPath, window.location.origin)
  url.searchParams.set(RELOAD_QUERY_KEY, serverVersion)
  return url.pathname + url.search + url.hash
}

/**
 * 화면 이동(path 가 바뀔 때) 직전에 부른다. 서버 버전이 내 버전과 다르면 이동하려던 경로로 전체 새로고침하고 true.
 * 그 밖(같음·확인 간격 안·실패·이미 이 버전으로 새로고침함)은 false — 호출부는 그대로 이동한다.
 */
export async function reloadIfNewVersion(toFullPath: string): Promise<boolean> {
  const mine = currentAppVersion()
  if (!mine) return false

  const now = Date.now()
  if (lastCheckedAt && now - lastCheckedAt < VERSION_CHECK_INTERVAL_MS) return false
  lastCheckedAt = now

  const server = await fetchServerVersion()
  if (!server || server === mine) return false
  if (readReloadedVersion() === server) return false

  markReloaded(server)
  if (readReloadedVersion() !== server) return false
  window.location.assign(reloadUrl(toFullPath, server))
  return true
}

// 새로고침으로 붙은 캐시 깨기 쿼리를 주소창에서 지운다 — 라우터가 현재 위치를 읽기 전에 부른다
export function removeReloadQueryFromUrl() {
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has(RELOAD_QUERY_KEY)) return
    url.searchParams.delete(RELOAD_QUERY_KEY)
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
  } catch {
    // 주소를 못 고쳐도 화면 동작에는 영향 없음
  }
}

// 테스트 전용
export function resetVersionCheckForTest() {
  lastCheckedAt = 0
}
